import * as React from 'react';
import { Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Bounds, useGLTF } from '@react-three/drei';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { getLocalModelObjectUrl, getProductModelObjectUrl } from '../../api/productV2Api';

function Model({ url }: { url: string }) {
  const { scene } = useGLTF(url);
  return <primitive object={scene} />;
}

interface Model3DPreviewDialogProps {
  open: boolean;
  /** Product UUID whose (decrypted) model to preview. */
  productUuid: string | null;
  /**
   * A model picked in the form but not uploaded yet. Previewed as is: until the
   * product is saved the server has no model for this uuid (ARDE-276).
   */
  localFile?: File;
  onClose: () => void;
}

const CENTERED = 'absolute inset-0 flex items-center justify-center';

/**
 * Previews a product's 3D model in a dialog. A file picked but not saved yet is shown
 * straight from the browser. Otherwise the model is fetched DECRYPTED from the
 * backoffice-only `model-plain` endpoint (the backoffice can't run the visualizer WASM),
 * turned into an object URL, and rendered with react-three-fiber.
 */
export function Model3DPreviewDialog({ open, productUuid, localFile, onClose }: Model3DPreviewDialogProps) {
  const { t } = useTranslation();
  const [url, setUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || (!productUuid && !localFile)) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUrl(null);
    (localFile ? getLocalModelObjectUrl(localFile) : getProductModelObjectUrl(productUuid!))
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load 3D model');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, productUuid, localFile]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="h-[70vh] gap-0 overflow-hidden p-0 sm:max-w-3xl"
      >
        {/* The model stage stays dark whatever the theme — a neutral backdrop
            is what makes an unlit GLTF readable. */}
        <div className="relative h-full bg-neutral-900">
          <DialogTitle className="sr-only">
            {t('PAGES.MY_PRODUCTS.MENU.PREVIEW')}
          </DialogTitle>

          <DialogClose asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('COMMON.CLOSE') as string}
              className="absolute top-2 end-2 z-10 text-white hover:bg-white/10 hover:text-white"
            >
              <X />
            </Button>
          </DialogClose>

          {loading && (
            <div className={CENTERED}>
              <Loader2 className="size-5 animate-spin text-white" />
            </div>
          )}
          {error && (
            <div className={CENTERED}>
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}
          {url && !error && (
            <Canvas camera={{ position: [0, 0, 5], fov: 50 }} style={{ height: '100%' }}>
              <ambientLight intensity={0.7} />
              <directionalLight position={[5, 5, 5]} intensity={1} />
              <directionalLight position={[-5, 2, -5]} intensity={0.4} />
              <React.Suspense fallback={null}>
                <Bounds fit clip observe margin={1.2}>
                  <Model url={url} />
                </Bounds>
              </React.Suspense>
              <OrbitControls makeDefault enablePan enableZoom />
            </Canvas>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
